import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ITableTileDefinition } from '../../../dashboards/interfaces/tiles/definitions/table-tile-definition.interface';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';
import { TileSetupWizardComponent } from '../tile-setup-wizard.component';

interface ITableColumnOption {
  name: string;
  displayName?: string;
  selected: boolean;
}

@Component({
  standalone: false,
  selector: 'app-table-setup-wizard',
  templateUrl: './table-setup-wizard.component.html',
  styleUrls: ['./table-setup-wizard.component.scss'],
})
export class TableSetupWizardComponent extends TileSetupWizardComponent<ITableTileDefinition> {
  private readonly destroy$ = new Subject<void>();

  errors = [];

  readonly setupForm = new FormGroup({
    title: new FormControl(),
    dataSource: new FormControl(),
    showColumns: new FormArray([]),
    showPageControls: new FormControl(true),
    showPageSizeOptions: new FormControl(true),
    openInDrawer: new FormControl(false),
  });

  tableShowColumnsOptions: ITableColumnOption[] = [];
  dataSourceObject;

  get titleControl(): AbstractControl {
    return this.setupForm.get('title') as AbstractControl;
  }

  get dataSourceControl(): AbstractControl {
    return this.setupForm.get('dataSource') as AbstractControl;
  }

  get showColumnsControl(): FormArray {
    return this.setupForm.get('showColumns') as FormArray;
  }

  get showColumnsString(): string {
    return this.showColumnsControl.value.join(', ');
  }

  constructor(private readonly tileCreatorService: TileCreatorService) {
    super();

    this.tileInstance = inject(TILE_REQUEST).tileInstance;

    this.dataSourceControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showColumnsControl.clear();
      this.tableShowColumnsOptions = [];
    });

    if (this.tileInstance) {
      this.setupForm.patchValue({
        title: this.tileInstance.title,
        showPageControls: this.tileInstance.showPageControls,
        showPageSizeOptions: this.tileInstance.showPageSizeOptions,
        openInDrawer: this.tileInstance.openInDrawer ?? false,
        dataSource: this.tileInstance.recipe?.dataSource,
      });

      this.tileInstance.showColumns?.forEach((showColumn) => {
        this.showColumnsControl.push(new FormControl(showColumn));
      });
    }
  }

  ngOnInit() {
    this.hookForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initTableShowColumnsOptions(dataSource: any) {
    this.dataSourceObject = dataSource;
    const currentOrder = this.showColumnsControl.value as string[];
    this.tableShowColumnsOptions = (dataSource?.properties ?? []).map((property) => ({
      ...property,
      selected: currentOrder.includes(property.name),
    }));
    this.syncSelectedColumns();
  }

  onColumnSelectionChange(columnName: string, selected: boolean) {
    const column = this.tableShowColumnsOptions.find(({ name }) => name === columnName);
    if (!column) {
      return;
    }

    column.selected = selected;
    this.syncSelectedColumns();
  }

  onColumnDrop(event: CdkDragDrop<ITableColumnOption[]>) {
    moveItemInArray(this.tableShowColumnsOptions, event.previousIndex, event.currentIndex);
    this.syncSelectedColumns();
  }

  onCreate() {
    this.errors = [];

    let showColumns = this.showColumnsControl.value;

    console.log(showColumns);

    if (showColumns.length == 0) {
      this.errors.push({ message: 'Please select at least one column' });
      return;
    }

    Object.assign(this.tileInstance, this.getChanges());

    this.tileCreatorService.onChange.next();
  }

  getChanges() {
    return {
      tileType: 'table',
      title: this.titleControl.value,
      showColumns: this.showColumnsControl.value,
      recipe: {
        dataSource: this.dataSourceControl.value,
      },
      canView: 'true',
      tableOptions: {
        canSort: false,
      },
      backgroundColor: 'white',
      defaultPageSize: 5,
      showPageControls: this.setupForm.controls.showPageControls.value,
      showPageSizeOptions: this.setupForm.controls.showPageSizeOptions.value,
      openInDrawer: this.setupForm.controls.openInDrawer.value,
    };
  }

  isChecked(name) {
    return this.showColumnsControl.value?.includes(name);
  }

  private syncSelectedColumns() {
    const selectedColumns = this.tableShowColumnsOptions
      .filter(({ selected }) => selected)
      .map(({ name }) => name);

    this.showColumnsControl.clear({ emitEvent: false });
    selectedColumns.forEach((column) => this.showColumnsControl.push(new FormControl(column)));
    this.showColumnsControl.updateValueAndValidity({ emitEvent: true });
    this.tempTileDefinitionChanged.emit(this.getChanges());
  }
}
